import React, { useEffect } from 'react';
import { Link, useMatch } from 'react-router-dom';
import * as Bixby from '$/core';
import { RESET_PASSWORD_SUCCESS_MESSAGE } from '$/core/constants';
import { AppState } from '$/store';
import * as ResetPasswordActions from '$/store/reset-password/actions';
import { connect } from 'react-redux';
import { ReactComponent as Logo } from '$/assets/BixLogo.svg';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import InputBlock from '$/components/global/inputBlock';
import Info from '$/components/global/info/Info';
import Error from '$/components/global/error/Error';
import './index.scss';
import Loading from '$/components/loading/Loading';
import { ResetPasswordRequest } from '$/core/models';

const resetPasswordRequestAction = ResetPasswordActions.resetPasswordRequest;
const verifyPasswordResetTokenAction = ResetPasswordActions.verifyResetPasswordRequest;

interface IPasswordResetRouteProps {
  userId?: string;
  code?: string;
}

export interface ResetPasswordValues {
  password: string;
  confirmPassword: string;
}

interface IResetPasswordProps extends IPasswordResetRouteProps {
  dispatchResetPasswordRequest: (ResetPasswordRequest: Bixby.Models.ResetPasswordRequest) => Promise<void>;
  dispatchVerifyResetPasswordRequest: (
    VerifyResetPasswordRequest: Bixby.Models.VerifyPasswordResetTokenRequest
  ) => Promise<void>;
  isLoading: boolean;
  error?: string;
  success: boolean;
  tokenInvalid: boolean;
}

const ResetPassword: React.FC<IResetPasswordProps> = props => {
  const { isLoading, error, success, tokenInvalid, dispatchResetPasswordRequest, dispatchVerifyResetPasswordRequest } =
    props;
  const match = useMatch('/resetPassword/:userId?/:code?');
  const initialPasswordValues: ResetPasswordValues = {
    password: '',
    confirmPassword: '',
  };
  useEffect(() => {
    const verifyToken = async () => {
      await dispatchVerifyResetPasswordRequest({
        userId: match?.params.userId ?? '',
        code: match?.params.code ?? '',
      });
    };
    verifyToken();
  }, [dispatchVerifyResetPasswordRequest, match]);

  const normalRequiredField = Yup.string().max(256, 'Too long').required();

  const schema = Yup.object({
    password: normalRequiredField
      .required('Password is required.')
      .min(8, 'Password is too short.')
      .matches(
        /^(?=^.{8,}$)(?=.*\d)(?=.*[A-Z])(?=.*[a-z]).*$/,
        'Must Contain at least 8 Characters, One Uppercase, One Lowercase, One Number'
      ),
    confirmPassword: normalRequiredField
      .required('Confirmed Password is required.')
      .oneOf([Yup.ref('password'), null as any], 'Passwords must match.'),
  });

  const passwordSuccess = success ? (
    <div>
      <Info>{RESET_PASSWORD_SUCCESS_MESSAGE}</Info>
      <Info>
        <Link className="link_underline" to={`/login`}>
          Login
        </Link>
      </Info>
    </div>
  ) : (
    <Formik
      initialValues={initialPasswordValues}
      validationSchema={schema}
      onSubmit={(values: ResetPasswordValues) => {
        const passwordReset: ResetPasswordRequest = {
          userId: match?.params.userId ?? '',
          newPassword: values.password,
          code: match?.params.code ?? '',
        };
        dispatchResetPasswordRequest(passwordReset);
      }}
      render={({ handleSubmit }) => (
        <form className="login_form reset_password_form" onSubmit={handleSubmit}>
          <InputBlock title="Password">
            <Field type="password" name="password" />
          </InputBlock>
          <ErrorMessage name="password">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
          <InputBlock title="Confirm Password">
            <Field type="password" name="confirmPassword" />
          </InputBlock>
          <ErrorMessage name="confirmPassword">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
          <div className="button_center">
            <button className="bix_button" type="submit">
              Reset Password
            </button>
          </div>
        </form>
      )}></Formik>
  );

  const display = isLoading ? (
    <div className="password_loading_spinner">
      <Loading color="primary" />
    </div>
  ) : tokenInvalid ? (
    <Error>Invalid token</Error>
  ) : error ? (
    <Error>{error}</Error>
  ) : (
    passwordSuccess
  );

  return (
    <div className="loginPage">
      <div className="content_wrap forgot_password_box">
        <div className="logo_welcome">
          <Logo />
          Reset your password
        </div>
        {display}
      </div>
      <div className="copy_foot">&copy; {new Date().getFullYear()} Bixby Research and Analytics LLC</div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.resetPasswordState.isLoading,
    error: state.resetPasswordState.error,
    success: state.resetPasswordState.success,
    tokenInvalid: state.resetPasswordState.tokenInvalid,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchResetPasswordRequest: (cmd: Bixby.Models.ResetPasswordRequest) => dispatch(resetPasswordRequestAction(cmd)),
  dispatchVerifyResetPasswordRequest: (cmd: Bixby.Models.VerifyPasswordResetTokenRequest) =>
    dispatch(verifyPasswordResetTokenAction(cmd)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
